import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DatePipe } from '@angular/common';

import { SoftwareUpdate } from '../../models/software-update.model';
import {
  RolloutDeviceRow,
  RolloutDeviceStatusFilter,
  RolloutSummaryData,
  RolloutTenantSortKey,
  RolloutTenantSummary,
} from '../../models/rollout.model';
import { BatchAssignmentData } from '../../models/batch-assignment.model';
import { SoftwareUpdateService } from '../../services/software-update.service';
import { Tenant } from '../../../admin/tenants/models/tenant.model';
import { TenantService } from '../../../admin/tenants/services/tenant.service';
import { Icon } from '../../../../shared/components/icon/icon';
import { Grid } from '../../../../shared/components/grid/grid';
import { GridCellDirective } from '../../../../shared/components/grid/directives/grid-cell.directive';
import { GridColumn, GridSort, GridTab, gridColumns } from '../../../../shared/components/grid/models/grid.model';
import { relativeTime } from '../../../../shared/utils/relative-time';
import { ToastService } from '../../../../shared/services/toast-service';
import { LabelBadge } from '../../../../shared/components/label-badge/label-badge';
import { tenantAccent, tenantHue } from '../../../../shared/utils/tenant-color';
import { assignmentGridStatus } from '../../utils/assignment-status.util';

/**
 * Rollout tab (handoff screen 4a) — the standing answer to "where is this
 * update across the fleet?". Summary tiles + per-tenant bars are hand-rolled
 * to match the wireframe; the device table underneath is the shared
 * `app-grid` (server-side tabs/search/pagination, same as the Software
 * Updates list) — its status/date/text renderers set the ceiling on exact
 * per-cell styling (e.g. dates come out as Angular's 'medium' format, not
 * the wireframe's short "Aug 19").
 *
 * Built against `SoftwareUpdateService`'s `getRolloutSummary()`
 * (`GET .../rollout`), `getRolloutTenants()` (`GET .../rollout/tenants`,
 * the By-tenant table) and `getRolloutDevices()` (`GET .../rollout/devices`)
 * — contracts per backend handoff.
 *
 * The By-tenant table has no tenant-name search (tenant names live only in
 * Keycloak, not the DB this endpoint reads — see the handoff), and its own
 * rows are NOT scoped by the header's `Tenant: all ▾` filter — the tenants
 * endpoint has no such param. Narrowing that filter only affects the
 * summary tiles and device table, so at the moment it's set, the by-tenant
 * table still shows every tenant. Worth revisiting if that reads as a bug
 * in practice rather than an accepted gap.
 *
 * "Refresh on tab focus" is approximated by re-fetching on every activation:
 * the shell's @switch recreates this component each time the tab is
 * selected, so ngOnInit already re-runs — no separate focus listener needed.
 */
@Component({
  selector: 'app-rollout-tab',
  standalone: true,
  imports: [DatePipe, Icon, Grid, GridCellDirective, LabelBadge],
  templateUrl: './rollout-tab.component.html',
  // See assign-devices-tab.component.ts for why: without this, the host
  // custom element doesn't stretch to fill the parent's flex column.
  host: { class: 'contents' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RolloutTabComponent implements OnInit {
  readonly update = input.required<SoftwareUpdate>();

  private readonly service = inject(SoftwareUpdateService);
  private readonly tenantService = inject(TenantService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  readonly allTenants = signal<Tenant[]>([]);
  readonly tenantFilter = signal<string | null>(null);
  readonly tenantMenuOpen = signal(false);

  readonly summary = signal<RolloutSummaryData | null>(null);
  readonly isLoadingSummary = signal(false);
  readonly isRetrying = signal(false);

  // ── Device grid (app-grid — server-driven: it renders what it's given and
  // emits intents; this component owns the state and refetches) ───────────
  readonly statusFilter = signal<RolloutDeviceStatusFilter>('all');
  readonly query = signal('');
  readonly devices = signal<RolloutDeviceRow[]>([]);
  readonly isLoadingDevices = signal(false);
  readonly page = signal(0);
  readonly pageSize = signal(20);
  readonly totalElements = signal(0);
  readonly totalPages = signal(0);

  readonly rowId = (row: RolloutDeviceRow) => row.clientId;
  /** Narrows the erased-generic row that projected grid templates receive. */
  readonly asDevice = (row: unknown): RolloutDeviceRow => row as RolloutDeviceRow;

  // ── By-tenant table (app-grid — separate paginated/sorted endpoint,
  // holds up at hundreds of tenants unlike the old fixed bar-per-tenant
  // list) ──────────────────────────────────────────────────────────────
  readonly tenantRows = signal<RolloutTenantSummary[]>([]);
  readonly isLoadingTenants = signal(false);
  readonly tenantPage = signal(0);
  readonly tenantPageSize = signal(20);
  readonly tenantTotalElements = signal(0);
  readonly tenantTotalPages = signal(0);
  /** null while the default `leastComplete` composite order is active — no
   *  single column owns that state, so no header shows a sort arrow for it. */
  readonly tenantGridSort = signal<GridSort | null>(null);

  readonly tenantRowId = (row: RolloutTenantSummary) => row.tenantId;
  readonly asTenantRow = (row: unknown): RolloutTenantSummary => row as RolloutTenantSummary;

  readonly tenantColumns: GridColumn<RolloutTenantSummary>[] = gridColumns<RolloutTenantSummary>([
    { key: 'tenantName', label: 'Tenant', type: 'template', alwaysVisible: true, width: 220 },
    {
      key: 'progress',
      label: 'Progress',
      type: 'template',
      width: 220,
      sortable: true,
      value: (row) => (row.fleetSize > 0 ? row.ok / row.fleetSize : 0),
    },
    { key: 'installed', label: 'Installed', type: 'template', width: 110, sortable: true, value: (row) => row.ok },
    {
      key: 'pending',
      label: 'Pending',
      type: 'template',
      width: 90,
      sortable: true,
      value: (row) => this.pendingSync(row),
    },
    { key: 'failed', label: 'Failed', type: 'template', width: 90, sortable: true, value: (row) => row.failed },
  ]);

  /** One tab per real status value — no folding here, unlike the "Pending
   *  sync" tile, so the tab someone clicks always matches exactly what the
   *  API filters by. Counts come straight from the summary already loaded,
   *  so they don't need a separate query.
   *
   * Only 4 tabs, matching the wireframe's pill count — app-grid's toolbar
   * row is a fixed 58px with a fixed-width search box and doesn't wrap, so
   * all 6 real status values plus "All" overflow and collide with it.
   * Downloading/Installing (rare, short-lived states) have no dedicated tab
   * as a result; they're still visible via the status tiles above and the
   * Status column, just not one-click filterable here. */
  readonly gridTabs = computed<GridTab[]>(() => {
    const s = this.summary();
    const counts = s?.statusCounts;
    return [
      { id: 'all', label: 'All', count: s?.fleetSize },
      { id: 'failed', label: 'Failed', count: counts?.failed },
      { id: 'pending', label: 'Pending', count: counts?.pending },
      { id: 'not_assigned', label: 'Not assigned', count: counts?.notAssigned },
    ];
  });

  readonly columns: GridColumn<RolloutDeviceRow>[] = gridColumns<RolloutDeviceRow>([
    { key: 'clientId', label: 'Device ID', type: 'template', alwaysVisible: true, width: 150 },
    { key: 'tenant', label: 'Tenant', type: 'template', width: 190, value: (row) => row.tenantName },
    {
      key: 'assignmentStatus',
      label: 'Status',
      type: 'status',
      width: 220,
      value: (row) => row.assignmentStatus,
      status: (_, row) => assignmentGridStatus(row.assignmentStatus, row.failedReason),
    },
    // 'template', not the built-in 'date' type: the grid's null-value copy
    // ("No results") is meant for whole-table empty states, not a single
    // empty cell, and reads as a bug here.
    { key: 'assignedAt', label: 'Assigned', type: 'template', width: 170, value: (row) => row.assignedAt },
    { key: 'updatedAt', label: 'Last changed', type: 'template', width: 130, value: (row) => row.updatedAt },
  ]);

  readonly tenantFilterLabel = computed(() => {
    const id = this.tenantFilter();
    if (!id) return 'Tenant: all';
    return `Tenant: ${this.allTenants().find((t) => t.id === id)?.name ?? id}`;
  });

  /** pending + downloading + installing — the backend keeps these granular,
   *  the "Pending sync" tile folds them for display purposes only. */
  pendingSync(counts: { pending: number; downloading: number; installing: number }): number {
    return counts.pending + counts.downloading + counts.installing;
  }

  ngOnInit(): void {
    this.tenantService.getTenants(0, 200).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => this.allTenants.set(res.data),
      error: () => this.toastService.show('Failed to load tenants', 'error'),
    });

    this.fetchSummary();
    this.fetchTenants();
    this.fetchDevices();
  }

  toggleTenantMenu(): void {
    this.tenantMenuOpen.update((v) => !v);
  }

  selectTenantFilter(tenantId: string | null): void {
    this.tenantFilter.set(tenantId);
    this.tenantMenuOpen.set(false);
    this.page.set(0);
    this.fetchSummary();
    this.fetchDevices();
  }

  onTabChange(id: string): void {
    this.statusFilter.set(id as RolloutDeviceStatusFilter);
    this.page.set(0);
    this.fetchDevices();
  }

  onSearchChange(term: string): void {
    this.query.set(term);
    this.page.set(0);
    this.fetchDevices();
  }

  onPageChange(page: number): void {
    this.page.set(page);
    this.fetchDevices();
  }

  onPageSizeChange(size: number): void {
    this.pageSize.set(size);
    this.page.set(0);
    this.fetchDevices();
  }

  // The device table has nothing meaningful to sort by yet (row order is
  // fixed clientId-ascending per the backend contract) — no columns are
  // marked sortable, so this should never actually fire.
  onSortChange(_sort: GridSort): void {}

  onTenantPageChange(page: number): void {
    this.tenantPage.set(page);
    this.fetchTenants();
  }

  onTenantPageSizeChange(size: number): void {
    this.tenantPageSize.set(size);
    this.tenantPage.set(0);
    this.fetchTenants();
  }

  /** Column key → the API's sort key; `tenantName` isn't sortable (no
   *  server-side name sort — see the class doc comment) so it never reaches here. */
  private static readonly TENANT_SORT_KEY: Partial<Record<string, RolloutTenantSortKey>> = {
    progress: 'progress',
    installed: 'installed',
    pending: 'pending',
    failed: 'failed',
  };

  onTenantSortChange(sort: GridSort): void {
    this.tenantGridSort.set(sort);
    const apiKey = RolloutTabComponent.TENANT_SORT_KEY[sort.sortBy] ?? 'progress';
    const suffix = sort.sortDirection === 'DESC' ? 'Desc' : 'Asc';
    this.tenantPage.set(0);
    this.fetchTenants(`${apiKey}${suffix}`);
  }

  private fetchSummary(): void {
    this.isLoadingSummary.set(true);
    this.service
      .getRolloutSummary(this.update().id, this.tenantFilter() ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.summary.set(res.data);
          this.isLoadingSummary.set(false);
        },
        error: () => {
          this.toastService.show('Failed to load rollout summary', 'error');
          this.isLoadingSummary.set(false);
        },
      });
  }

  private fetchDevices(): void {
    this.isLoadingDevices.set(true);
    this.service
      .getRolloutDevices(this.update().id, {
        status: this.statusFilter(),
        q: this.query() || undefined,
        tenantId: this.tenantFilter() ?? undefined,
        page: this.page(),
        size: this.pageSize(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.devices.set(res.data);
          this.totalElements.set(res.totalElements);
          this.totalPages.set(res.totalPages);
          this.isLoadingDevices.set(false);
        },
        error: () => {
          this.toastService.show('Failed to load devices', 'error');
          this.isLoadingDevices.set(false);
        },
      });
  }

  /** @param sort e.g. 'installedDesc'; omit to (re)fetch under the current sort. */
  private fetchTenants(sort?: string): void {
    this.isLoadingTenants.set(true);
    this.service
      .getRolloutTenants(this.update().id, {
        sort,
        page: this.tenantPage(),
        size: this.tenantPageSize(),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.tenantRows.set(res.data);
          this.tenantTotalElements.set(res.totalElements);
          this.tenantTotalPages.set(res.totalPages);
          this.isLoadingTenants.set(false);
        },
        error: () => {
          this.toastService.show('Failed to load tenants', 'error');
          this.isLoadingTenants.set(false);
        },
      });
  }

  retryFailed(): void {
    if (this.isRetrying()) return;
    this.isRetrying.set(true);
    this.service
      .retryFailed(this.update().id, this.tenantFilter() ?? undefined)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.isRetrying.set(false);
          this.showRetryToast(res.data);
          this.fetchSummary();
          this.page.set(0);
          this.fetchDevices();
        },
        error: () => {
          this.isRetrying.set(false);
          this.toastService.show('Failed to retry devices', 'error');
        },
      });
  }

  private showRetryToast(result: BatchAssignmentData): void {
    if (result.requested === 0) {
      this.toastService.show('No devices were eligible for retry', 'error');
    } else if (result.failed === 0) {
      this.toastService.show(`Retried ${result.assigned} devices successfully`, 'success');
    } else {
      this.toastService.show(
        `Retried ${result.requested} devices — ${result.assigned} succeeded, ${result.failed} failed again`,
        'error',
      );
    }
  }

  tenantDot(tenantId: string): string {
    return tenantAccent(tenantId).dot;
  }

  /** Hue for the tenant identity pill — same deterministic mapping as tenantDot(). */
  tenantHueOf(tenantId: string) {
    return tenantHue(tenantId);
  }

  /** "not assigned yet" copy per the wireframe, vs. a real progress bar. */
  hasAnyAssignment(tenant: RolloutTenantSummary): boolean {
    return tenant.notAssigned < tenant.fleetSize;
  }

  barWidthPct(count: number, fleetSize: number): number {
    return fleetSize > 0 ? (count / fleetSize) * 100 : 0;
  }

  /** Completion percentage label next to the by-tenant progress bar. */
  progressPct(tenant: RolloutTenantSummary): number {
    return tenant.fleetSize > 0 ? Math.round((tenant.ok / tenant.fleetSize) * 100) : 0;
  }

  /** For the "Last changed" column. See `relativeTime`'s doc comment for
   *  why this isn't run through Transloco. */
  readonly relativeTime = relativeTime;
}
